import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Loader2, Calculator, DollarSign, Scale, AlertTriangle,
  ChevronRight, TrendingUp, Target, Gavel, Zap,
  ArrowUpRight, ArrowDownRight, BarChart3, Trophy
} from "lucide-react";
import { API } from "@/constants";
import SmartSelect, { CASE_TYPE_OPTIONS, JURISDICTION_OPTIONS, INJURY_TYPE_OPTIONS } from "@/components/shared/SmartSelect";

const fmt = (n) => typeof n === "number" ? `$${n.toLocaleString()}` : n;

const InsightList = ({ items, icon: Icon = ChevronRight, color = "text-blue-500" }) => (
  <div className="space-y-2">
    {(items || []).map((item, i) => (
      <div key={i} className="flex items-start gap-2.5 text-sm">
        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${color}`} />
        <span className="text-slate-700">{typeof item === "string" ? item : JSON.stringify(item)}</span>
      </div>
    ))}
  </div>
);

const StatBox = ({ label, value, color = "bg-slate-50", textColor = "text-slate-800" }) => (
  <div className={`p-4 rounded-2xl ${color} text-center`}>
    <p className={`text-2xl font-black ${textColor}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-1">{label}</p>
  </div>
);

const DamagesCalculatorPage = () => {
  const [form, setForm] = useState({
    case_type: "", medical_expenses: 25000, future_medical: 50000,
    lost_wages: 15000, future_lost_wages: 100000, property_damage: 5000,
    other_economic: 0, discount_rate: 0.03, future_years: 10,
    severity: 6, duration_months: 24, daily_impact_rate: 200,
    plaintiff_age: 42, include_punitive: false, defendant_wealth: 0,
    reprehensibility: 5, jurisdiction: "", description: ""
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("input");

  // Benchmark state
  const [benchForm, setBenchForm] = useState({ case_type: "", jurisdiction: "", total_damages: 500000, economic_damages: 200000, non_economic_damages: 300000, severity: 6, injury_type: "", description: "" });
  const [benchLoading, setBenchLoading] = useState(false);
  const [benchData, setBenchData] = useState(null);

  const run = async () => {
    if (!form.case_type) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/damages/calculate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          medical_expenses: Number(form.medical_expenses),
          future_medical: Number(form.future_medical),
          lost_wages: Number(form.lost_wages),
          future_lost_wages: Number(form.future_lost_wages),
          property_damage: Number(form.property_damage),
          other_economic: Number(form.other_economic),
          discount_rate: Number(form.discount_rate),
          future_years: Number(form.future_years),
          severity: Number(form.severity),
          duration_months: Number(form.duration_months),
          daily_impact_rate: Number(form.daily_impact_rate),
          plaintiff_age: Number(form.plaintiff_age),
          defendant_wealth: Number(form.defendant_wealth),
          reprehensibility: Number(form.reprehensibility),
        })
      });
      const j = await r.json();
      setData(j.data);
      setActiveTab("results");
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const runBenchmark = async () => {
    if (!benchForm.case_type) return;
    setBenchLoading(true);
    try {
      const r = await fetch(`${API}/damages/verdict-benchmark`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...benchForm,
          total_damages: Number(benchForm.total_damages),
          economic_damages: Number(benchForm.economic_damages),
          non_economic_damages: Number(benchForm.non_economic_damages),
          severity: Number(benchForm.severity),
        })
      });
      const j = await r.json();
      setBenchData(j.data);
    } catch (e) { console.error(e); } finally { setBenchLoading(false); }
  };

  const u = (k, v) => setForm({ ...form, [k]: v });
  const eco = data?.economic_damages;
  const ne = data?.non_economic_damages;
  const pun = data?.punitive_damages;
  const ai = data?.ai_analysis;

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-6" data-testid="damages-calculator">
      {/* Header */}
      <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #7C2D12, #C2410C)" }}>
        <div className="flex items-center gap-3 mb-1">
          <Calculator className="h-7 w-7 text-orange-300" />
          <h1 className="text-2xl font-black tracking-tight">Damages Calculator</h1>
          <Badge className="bg-orange-500/20 text-orange-200 border-orange-500/30">FORENSIC</Badge>
        </div>
        <p className="text-orange-200 text-sm">Economic, non-economic & punitive damages with verdict benchmarking and settlement leverage</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 h-auto p-1">
          <TabsTrigger value="input" className="text-xs py-2 data-[state=active]:bg-orange-600 data-[state=active]:text-white">
            <Calculator className="h-3.5 w-3.5 mr-1.5" />Calculate
          </TabsTrigger>
          <TabsTrigger value="results" className="text-xs py-2 data-[state=active]:bg-orange-600 data-[state=active]:text-white" disabled={!data}>
            <BarChart3 className="h-3.5 w-3.5 mr-1.5" />Results
          </TabsTrigger>
          <TabsTrigger value="benchmark" className="text-xs py-2 data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Trophy className="h-3.5 w-3.5 mr-1.5" />Benchmark
          </TabsTrigger>
        </TabsList>

        {/* INPUT TAB */}
        <TabsContent value="input">
          <div className="space-y-6">
            {/* Case Info */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Scale className="h-4 w-4 text-orange-600" />Case Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Case Type *</Label>
                    <div className="mt-1"><SmartSelect value={form.case_type} onValueChange={v => u("case_type", v)} options={CASE_TYPE_OPTIONS} placeholder="Select case type..." data-testid="dmg-case-type" /></div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Jurisdiction</Label>
                    <div className="mt-1"><SmartSelect value={form.jurisdiction} onValueChange={v => u("jurisdiction", v)} options={JURISDICTION_OPTIONS} placeholder="Select jurisdiction..." data-testid="dmg-jurisdiction" /></div>
                  </div>
                  <div><Label className="text-xs font-semibold">Plaintiff Age</Label><Input type="number" value={form.plaintiff_age} onChange={e => u("plaintiff_age", e.target.value)} className="mt-1" /></div>
                </div>
                <div><Label className="text-xs font-semibold">Case Description</Label><Textarea value={form.description} onChange={e => u("description", e.target.value)} placeholder="Brief case facts..." className="mt-1 min-h-[60px]" /></div>
              </CardContent>
            </Card>

            {/* Economic Damages */}
            <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4 text-blue-600" />Economic Damages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div><Label className="text-xs font-semibold">Medical Expenses ($)</Label><Input type="number" value={form.medical_expenses} onChange={e => u("medical_expenses", e.target.value)} className="mt-1" data-testid="dmg-medical" /></div>
                  <div><Label className="text-xs font-semibold">Future Medical ($)</Label><Input type="number" value={form.future_medical} onChange={e => u("future_medical", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs font-semibold">Lost Wages ($)</Label><Input type="number" value={form.lost_wages} onChange={e => u("lost_wages", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs font-semibold">Future Lost Wages ($)</Label><Input type="number" value={form.future_lost_wages} onChange={e => u("future_lost_wages", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs font-semibold">Property Damage ($)</Label><Input type="number" value={form.property_damage} onChange={e => u("property_damage", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs font-semibold">Other Economic ($)</Label><Input type="number" value={form.other_economic} onChange={e => u("other_economic", e.target.value)} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div><Label className="text-xs font-semibold">Discount Rate</Label><Input type="number" step="0.01" value={form.discount_rate} onChange={e => u("discount_rate", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs font-semibold">Future Years</Label><Input type="number" value={form.future_years} onChange={e => u("future_years", e.target.value)} className="mt-1" /></div>
                </div>
              </CardContent>
            </Card>

            {/* Non-Economic Damages */}
            <Card className="border-0 shadow-sm border-l-4 border-l-purple-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Scale className="h-4 w-4 text-purple-600" />Non-Economic Damages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Severity (1-10): {form.severity}</Label>
                    <input type="range" min="1" max="10" value={form.severity} onChange={e => u("severity", e.target.value)} className="w-full mt-2 accent-purple-600" data-testid="dmg-severity" />
                  </div>
                  <div><Label className="text-xs font-semibold">Duration (months)</Label><Input type="number" value={form.duration_months} onChange={e => u("duration_months", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs font-semibold">Daily Impact Rate ($)</Label><Input type="number" value={form.daily_impact_rate} onChange={e => u("daily_impact_rate", e.target.value)} className="mt-1" /></div>
                </div>
              </CardContent>
            </Card>

            {/* Punitive Damages */}
            <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><Gavel className="h-4 w-4 text-red-600" />Punitive Damages</CardTitle>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Include Punitive</Label>
                    <Switch checked={form.include_punitive} onCheckedChange={v => u("include_punitive", v)} data-testid="dmg-punitive-toggle" />
                  </div>
                </div>
              </CardHeader>
              {form.include_punitive && (
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs font-semibold">Defendant Wealth ($)</Label><Input type="number" value={form.defendant_wealth} onChange={e => u("defendant_wealth", e.target.value)} className="mt-1" /></div>
                    <div>
                      <Label className="text-xs font-semibold">Reprehensibility (1-10): {form.reprehensibility}</Label>
                      <input type="range" min="1" max="10" value={form.reprehensibility} onChange={e => u("reprehensibility", e.target.value)} className="w-full mt-2 accent-red-600" />
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            <Button onClick={run} disabled={loading || !form.case_type} className="w-full bg-orange-600 hover:bg-orange-700 h-12 text-base" data-testid="dmg-calculate-btn">
              {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Calculating Damages...</> : <><Calculator className="mr-2 h-5 w-5" />Calculate Full Damages</>}
            </Button>
          </div>
        </TabsContent>

        {/* RESULTS TAB */}
        <TabsContent value="results">
          {data && (
            <div className="space-y-5 animate-in fade-in duration-500">
              <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #7C2D12, #EA580C)" }}>
                <p className="text-xs uppercase tracking-widest text-orange-200 mb-3">Total Damages Estimate</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div><p className="text-xs text-orange-200">Compensatory</p><p className="text-2xl font-black">{fmt(data.total_compensatory)}</p></div>
                  <div><p className="text-xs text-orange-200">Grand Total</p><p className="text-3xl font-black">{fmt(data.grand_total)}</p></div>
                  <div><p className="text-xs text-orange-200">Punitive</p><p className="text-2xl font-black">{pun ? fmt(pun.recommended_punitive) : "N/A"}</p></div>
                </div>
              </div>
              {eco && (
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4 text-blue-600" />Economic Damages Breakdown</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <StatBox label="Past Medical" value={fmt(eco.past_medical)} color="bg-blue-50" textColor="text-blue-800" />
                      <StatBox label="Past Wages" value={fmt(eco.past_lost_wages)} color="bg-blue-50" textColor="text-blue-800" />
                      <StatBox label="Property" value={fmt(eco.property_damage)} color="bg-blue-50" textColor="text-blue-800" />
                      <StatBox label="Future Medical (PV)" value={fmt(eco.future_medical_pv)} color="bg-cyan-50" textColor="text-cyan-800" />
                      <StatBox label="Future Wages (PV)" value={fmt(eco.future_lost_wages_pv)} color="bg-cyan-50" textColor="text-cyan-800" />
                      <StatBox label="Total Economic" value={fmt(eco.total_economic)} color="bg-emerald-50" textColor="text-emerald-800" />
                    </div>
                    <p className="text-xs text-slate-500 mt-3 text-center">Discount Rate: {(eco.discount_rate * 100).toFixed(1)}% over {eco.future_years} years</p>
                  </CardContent>
                </Card>
              )}
              {ne && (
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Scale className="h-4 w-4 text-purple-600" />Non-Economic Damages</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <StatBox label="Multiplier Method" value={fmt(data.non_economic_multiplier_estimate)} color="bg-purple-50" textColor="text-purple-800" />
                      <StatBox label="Per Diem Method" value={fmt(data.non_economic_per_diem_estimate)} color="bg-violet-50" textColor="text-violet-800" />
                      <StatBox label="Composite Multiplier" value={`${ne.composite_multiplier}x`} color="bg-fuchsia-50" textColor="text-fuchsia-800" />
                      <StatBox label="Per Diem Lifetime" value={fmt(ne.per_diem_lifetime)} color="bg-pink-50" textColor="text-pink-800" />
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 text-sm text-slate-600"><p className="font-semibold text-slate-700 mb-1">Formula</p><p>{ne.formula_explanation}</p></div>
                  </CardContent>
                </Card>
              )}
              {pun && (
                <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Gavel className="h-4 w-4 text-red-600" />Punitive Damages (BMW v. Gore)</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <StatBox label="Ratio-Based" value={fmt(pun.ratio_based)} color="bg-red-50" textColor="text-red-800" />
                      <StatBox label="Ratio Used" value={pun.ratio_used} color="bg-red-50" textColor="text-red-700" />
                      <StatBox label="Recommended" value={fmt(pun.recommended_punitive)} color="bg-orange-50" textColor="text-orange-800" />
                      <StatBox label="Conservative" value={fmt(pun.conservative_punitive)} color="bg-amber-50" textColor="text-amber-800" />
                    </div>
                    <p className="text-xs text-slate-500 italic text-center">{pun.constitutional_cap_note}</p>
                  </CardContent>
                </Card>
              )}
              {ai && (
                <div className="space-y-4">
                  {ai.verdict_range && (
                    <Card className="border-0 shadow-sm" style={{ background: "linear-gradient(135deg, #faf5ff, #fdf4ff)" }}>
                      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="h-4 w-4 text-purple-600" />AI Verdict Range Prediction</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-3">
                          <StatBox label="Low" value={fmt(ai.verdict_range.low)} color="bg-white/70" textColor="text-slate-700" />
                          <StatBox label="Likely" value={fmt(ai.verdict_range.likely)} color="bg-purple-100" textColor="text-purple-800" />
                          <StatBox label="High" value={fmt(ai.verdict_range.high)} color="bg-white/70" textColor="text-slate-700" />
                        </div>
                        {ai.negotiation_anchor && <p className="text-center mt-3 text-sm"><span className="text-slate-500">Negotiation Anchor:</span> <span className="font-bold text-purple-700">{fmt(ai.negotiation_anchor)}</span></p>}
                      </CardContent>
                    </Card>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                    {ai.strategic_notes && <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-emerald-600" />Strategic Notes</CardTitle></CardHeader><CardContent><InsightList items={ai.strategic_notes} color="text-emerald-500" /></CardContent></Card>}
                    {ai.risk_factors && <Card className="border-0 shadow-sm border-l-4 border-l-amber-500"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" />Risk Factors</CardTitle></CardHeader><CardContent><InsightList items={ai.risk_factors} icon={AlertTriangle} color="text-amber-500" /></CardContent></Card>}
                  </div>
                  {ai.comparable_verdicts && (
                    <Card className="border-0 shadow-sm">
                      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-blue-600" />Comparable Verdicts</CardTitle></CardHeader>
                      <CardContent><div className="space-y-2">{ai.comparable_verdicts.map((v, i) => (<div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50"><div className="min-w-0"><p className="text-sm font-medium truncate">{v.case}</p><p className="text-xs text-slate-500">{v.relevance}</p></div><Badge className="bg-blue-100 text-blue-700 shrink-0 ml-2">{fmt(v.amount)}</Badge></div>))}</div></CardContent>
                    </Card>
                  )}
                  {ai.jury_appeal_factors && <Card className="border-0 shadow-sm border-l-4 border-l-indigo-500"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-indigo-600" />Jury Appeal Factors</CardTitle></CardHeader><CardContent><InsightList items={ai.jury_appeal_factors} color="text-indigo-500" /></CardContent></Card>}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* BENCHMARK TAB */}
        <TabsContent value="benchmark">
          <div className="space-y-6">
            <Card className="border-0 shadow-sm" style={{ background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)" }}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-2 mb-2"><Trophy className="h-5 w-5 text-indigo-600" /><p className="text-sm font-bold text-indigo-900">Verdict Benchmarking & Settlement Leverage</p></div>
                <p className="text-xs text-indigo-700">Compare your case damages against jurisdiction-specific verdict databases. Get percentile rankings and settlement negotiation leverage.</p>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Case Type *</Label>
                <div className="mt-1"><SmartSelect value={benchForm.case_type} onValueChange={v => setBenchForm({ ...benchForm, case_type: v })} options={CASE_TYPE_OPTIONS} placeholder="Select case type..." data-testid="bench-case-type" /></div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Jurisdiction</Label>
                <div className="mt-1"><SmartSelect value={benchForm.jurisdiction} onValueChange={v => setBenchForm({ ...benchForm, jurisdiction: v })} options={JURISDICTION_OPTIONS} placeholder="Select jurisdiction..." data-testid="bench-jurisdiction" /></div>
              </div>
              <div><Label className="text-xs font-semibold">Total Damages ($)</Label><Input type="number" value={benchForm.total_damages} onChange={e => setBenchForm({ ...benchForm, total_damages: e.target.value })} className="mt-1" data-testid="bench-total" /></div>
              <div><Label className="text-xs font-semibold">Economic Damages ($)</Label><Input type="number" value={benchForm.economic_damages} onChange={e => setBenchForm({ ...benchForm, economic_damages: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs font-semibold">Non-Economic Damages ($)</Label><Input type="number" value={benchForm.non_economic_damages} onChange={e => setBenchForm({ ...benchForm, non_economic_damages: e.target.value })} className="mt-1" /></div>
              <div>
                <Label className="text-xs font-semibold">Severity (1-10): {benchForm.severity}</Label>
                <input type="range" min="1" max="10" value={benchForm.severity} onChange={e => setBenchForm({ ...benchForm, severity: e.target.value })} className="w-full mt-2 accent-indigo-600" data-testid="bench-severity" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Injury Type</Label>
                <div className="mt-1"><SmartSelect value={benchForm.injury_type} onValueChange={v => setBenchForm({ ...benchForm, injury_type: v })} options={INJURY_TYPE_OPTIONS} placeholder="Select injury type..." /></div>
              </div>
            </div>
            <div><Label className="text-xs font-semibold">Case Description</Label><Textarea value={benchForm.description} onChange={e => setBenchForm({ ...benchForm, description: e.target.value })} placeholder="Brief case facts for AI analysis..." className="mt-1 min-h-[60px]" /></div>
            <Button onClick={runBenchmark} disabled={benchLoading || !benchForm.case_type} className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-base" data-testid="bench-analyze-btn">
              {benchLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Benchmarking...</> : <><Trophy className="mr-2 h-5 w-5" />Benchmark Against Verdicts</>}
            </Button>

            {benchData && (
              <div className="space-y-5 animate-in fade-in duration-500">
                {/* Percentile Hero */}
                <div className="rounded-2xl p-6 text-white" style={{ background: benchData.percentile >= 70 ? "linear-gradient(135deg, #065F46, #059669)" : benchData.percentile >= 40 ? "linear-gradient(135deg, #78350F, #D97706)" : "linear-gradient(135deg, #7F1D1D, #DC2626)" }}>
                  <p className="text-xs uppercase tracking-widest opacity-80 mb-3">Your Case Percentile</p>
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <p className="text-7xl font-black">{benchData.percentile}<sup className="text-2xl">th</sup></p>
                      <p className="text-sm opacity-80 mt-1">percentile vs comparable verdicts</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-3">
                    <Badge className="bg-white/20 text-white">Your: {fmt(benchData.your_damages)}</Badge>
                    <Badge className="bg-white/20 text-white">Median: {fmt(benchData.distribution?.median)}</Badge>
                  </div>
                </div>

                {/* Distribution */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-indigo-600" />Verdict Distribution</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { label: "25th Percentile", value: benchData.distribution?.p25, color: "bg-slate-200" },
                        { label: "Median (50th)", value: benchData.distribution?.median, color: "bg-blue-400" },
                        { label: "Mean Average", value: benchData.distribution?.mean, color: "bg-indigo-400" },
                        { label: "75th Percentile", value: benchData.distribution?.p75, color: "bg-purple-400" },
                        { label: "90th Percentile", value: benchData.distribution?.p90, color: "bg-pink-400" },
                      ].map((item, i) => {
                        const maxVal = benchData.distribution?.p90 || 1;
                        const pct = Math.min((item.value / maxVal) * 100, 100);
                        return (
                          <div key={i}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-slate-600 font-medium">{item.label}</span>
                              <span className="font-bold">{fmt(item.value)}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${item.color}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      {/* Your case marker */}
                      <div className="mt-2 p-3 rounded-xl bg-orange-50 border border-orange-200">
                        <div className="flex justify-between text-xs">
                          <span className="text-orange-700 font-bold">Your Case</span>
                          <span className="font-black text-orange-800">{fmt(benchData.your_damages)}</span>
                        </div>
                        <div className="h-3 bg-orange-100 rounded-full overflow-hidden mt-1">
                          <div className="h-full rounded-full bg-orange-500" style={{ width: `${Math.min((benchData.your_damages / (benchData.distribution?.p90 || 1)) * 100, 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Settlement Leverage */}
                {benchData.settlement_leverage && (
                  <Card className="border-0 shadow-sm" style={{ background: benchData.settlement_leverage.position === "strong" ? "linear-gradient(135deg, #ECFDF5, #D1FAE5)" : benchData.settlement_leverage.position === "moderate" ? "linear-gradient(135deg, #FFFBEB, #FEF3C7)" : "linear-gradient(135deg, #FEF2F2, #FECACA)" }}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Target className="h-4 w-4" />Settlement Leverage
                        <Badge className={`${benchData.settlement_leverage.position === "strong" ? "bg-emerald-100 text-emerald-700" : benchData.settlement_leverage.position === "moderate" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {benchData.settlement_leverage.position?.toUpperCase()}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <p className="text-xs text-slate-500 mb-1">Leverage Score</p>
                          <Progress value={benchData.settlement_leverage.score} className="h-2.5" />
                        </div>
                        <span className="text-xl font-black text-slate-800">{benchData.settlement_leverage.score}/100</span>
                      </div>
                      <p className="text-sm text-slate-700">{benchData.settlement_leverage.note}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <StatBox label="Settlement Floor" value={fmt(benchData.settlement_leverage.recommended_range?.low)} color="bg-white/70" textColor="text-slate-800" />
                        <StatBox label="Settlement Target" value={fmt(benchData.settlement_leverage.recommended_range?.high)} color="bg-white/70" textColor="text-emerald-800" />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Jurisdiction & Severity Factors */}
                <div className="grid grid-cols-2 gap-3">
                  <StatBox label="Jurisdiction Factor" value={`${benchData.jurisdiction_factor}x`} color="bg-indigo-50" textColor="text-indigo-800" />
                  <StatBox label="Severity Factor" value={`${benchData.severity_factor}x`} color="bg-purple-50" textColor="text-purple-800" />
                </div>

                {/* AI Benchmark Analysis */}
                {benchData.ai_analysis && (
                  <div className="space-y-4">
                    {benchData.ai_analysis.comparable_verdicts && (
                      <Card className="border-0 shadow-sm">
                        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-indigo-600" />Comparable Verdicts</CardTitle></CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {benchData.ai_analysis.comparable_verdicts.map((v, i) => (
                              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium truncate">{v.case_name}</p>
                                  <p className="text-xs text-slate-500">{v.jurisdiction} {v.year && `(${v.year})`} — {v.relevance}</p>
                                </div>
                                <Badge className="bg-indigo-100 text-indigo-700 shrink-0 ml-2">{fmt(v.amount)}</Badge>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {benchData.ai_analysis.verdict_trend && (
                      <Card className="border-0 shadow-sm bg-slate-50">
                        <CardContent className="pt-5"><p className="text-sm"><span className="font-semibold text-slate-800">Verdict Trend: </span><span className="text-slate-600">{benchData.ai_analysis.verdict_trend}</span></p></CardContent>
                      </Card>
                    )}
                    <div className="grid md:grid-cols-2 gap-4">
                      {benchData.ai_analysis.settlement_leverage_points && (
                        <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500">
                          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ArrowUpRight className="h-4 w-4 text-emerald-600" />Settlement Leverage Points</CardTitle></CardHeader>
                          <CardContent><InsightList items={benchData.ai_analysis.settlement_leverage_points} color="text-emerald-500" /></CardContent>
                        </Card>
                      )}
                      {benchData.ai_analysis.defense_counter_arguments && (
                        <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
                          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" />Defense Counter-Arguments</CardTitle></CardHeader>
                          <CardContent><InsightList items={benchData.ai_analysis.defense_counter_arguments} icon={AlertTriangle} color="text-red-500" /></CardContent>
                        </Card>
                      )}
                    </div>
                    {benchData.ai_analysis.maximization_strategies && (
                      <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
                        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-blue-600" />Maximization Strategies</CardTitle></CardHeader>
                        <CardContent><InsightList items={benchData.ai_analysis.maximization_strategies} color="text-blue-500" /></CardContent>
                      </Card>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      {benchData.ai_analysis.optimal_demand && <StatBox label="Optimal Opening Demand" value={fmt(benchData.ai_analysis.optimal_demand)} color="bg-emerald-50" textColor="text-emerald-800" />}
                      {benchData.ai_analysis.walk_away_floor && <StatBox label="Walk-Away Floor" value={fmt(benchData.ai_analysis.walk_away_floor)} color="bg-red-50" textColor="text-red-800" />}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DamagesCalculatorPage;
